import BackButton from '@/components/BackButton';
import Button from '@/components/Button';
import { Breakpoint } from '@/components/GameBreakpoint';
import { GameFooter } from '@/components/GameFooter';
import GameHeader from '@/components/GameHeader';
import {
  GameProps,
  GameState,
  GameStepsComponents,
} from '@/components/Gameinterfaces';
import GameStepCoupons from '@/components/GameStepCoupons';
import GameStepDetails from '@/components/GameStepDetails';
import GameStepSubmit from '@/components/GameStepSubmit';
import GameStepSummary from '@/components/GameStepSummary';
import Header from '@/components/Header';
import { Icon, IconVariant } from '@/components/Icon';
import Modal from '@/components/Modal';
import ModalGameSwitch from '@/components/ModalGameSwitch';
import ModalInvalidCouponsInfo from '@/components/ModalInvalidCouponsInfo';
import StepTransition, { TransitionVariant } from '@/components/StepTransition';
import { gluecksSpirale } from '@/config/game/gluecksSpirale';
import { ID_EUROJACKPOT } from '@/config/game/ids';
import { GameStep } from '@/config/game/steps';
import { calculateCouponProgress } from '@/helpers/calculateCouponProgress';
import {
  calculateGamePrice,
  GamePriceType,
} from '@/helpers/calculateGamePrice';
import { countFilledCoupons } from '@/helpers/countFilledCoupons';
import GameQueryParamsParser from '@/helpers/GameQueryParamsParser';
import { getNextDrawDay } from '@/helpers/getNextJackpot';
import { fetchGameState } from '@/store/store';
import getRandomNumberArray from '@/utilities/getRandomNumberArray';
import isIOS from '@/utilities/isIOS';
import pushDatalayer, { DataLayerEvent } from '@/utilities/pushDatalayer';
import setFocusIfElementExists from '@/utilities/setFocusIfElementExists';
import { withRouter } from 'next/router';
import React from 'react';

const STEP_COMPONENTS: GameStepsComponents = {
  [GameStep.Coupons]: GameStepCoupons,
  [GameStep.Details]: GameStepDetails,
  [GameStep.Summary]: GameStepSummary,
  [GameStep.Submit]: GameStepSubmit,
};

enum StepDirection {
  Next,
  Prev,
}

class Game extends React.Component<GameProps, GameState> {
  private stepTransition: TransitionVariant = TransitionVariant.Toggle;

  constructor(props: GameProps) {
    super(props);
    this.state = {
      quickTipResponses: { statistics: null, shortinfo: null },
    };
  }

  private setFocusOnChange = () => {
    if (this.props.currentStep === GameStep.Coupons) {
      setFocusIfElementExists('#cuponModalOpener');
    }
    if (this.props.currentStep === GameStep.Summary) {
      setFocusIfElementExists('button[type="submit"]');
    }
  };

  private updateCurrentStepConditionally = () => {
    if (this.props.id === 2) {
      this.updateCurrentStep(GameStep.Details);
    }
  };

  private getStepComponentsVariant = () => {
    return STEP_COMPONENTS;
  };

  public handlePrevStep = (): void => {
    const prev = this.getStep(StepDirection.Prev);

    if (!prev && this.props.currentCouponId === 0) {
      this.goBackBeyondFirstStep();
    } else {
      if (this.isInvalidCoupon) {
        return Modal.open('ModalInvalidCouponsInfo');
      }
      this.props.onCurrentCouponIdUpdate(this.props.id, 0);
    }

    if (prev) {
      this.updateStepTransition(TransitionVariant.SlideRight);
      this.updateCurrentStep(prev);
    }
  };

  public componentDidMount() {
    this.updateActualViewSize();
    window.addEventListener('resize', this.handleResize, { passive: true });

    if (this.props.id) {
      fetchGameState(this.props.id)
        // Do not remove this, this fixes a race condition
        .then(() => {
          return new Promise((r) => {
            setTimeout(r, 0);
          });
        })
        .then(() => {
          this.updateCurrentDrawDay();
          this.updatePlayNumber();
          this.parseQueryParams();
          this.updateCurrentStepConditionally();

          pushDatalayer(
            `virtPath`,
            `${
              this.props.urls.game
            }/${this.props.currentStep.toLocaleLowerCase()}`,
            DataLayerEvent.VirtualPageview
          );
        });
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  public componentDidUpdate() {
    const obj = {
      height: document.body.scrollHeight,
    };

    if (window.parent) {
      window.parent.postMessage(obj, '*');
    }
    this.setFocusOnChange();
  }

  public render(): JSX.Element {
    const {
      id,
      title,
      chanceDesc,
      logo,
      logoGame,
      currentStep,
      shortname,
      external,
      iframe,
      gameSwitch,
      seoDescription,
      seoHeadline,
    } = this.props;
    const gameSwitchList = iframe ? gameSwitch : [];
    const transition = this.stepTransition;
    const props = {
      ...this.props,
      handleNextStep: this.handleNextStep,
      gamePrice: this.gamePrice,
    };

    return (
      <div className="Game">
        <div className="Game__main">
          {currentStep === GameStep.Submit && !external && !iframe ? (
            <Header
              backButton={<BackButton onClick={this.handlePrevStep} />}
              hideNav
            />
          ) : (
              <GameHeader
                gameName={title}
                gameId={id}
                shortname={shortname}
                jackpotDesc={this.jackpotDefaultLabel}
                chanceDesc={chanceDesc}
                logo={logoGame || logo}
                onClickBackButton={this.handlePrevStep}
                backButtonLabel={this.backButtonLabel}
                gameSwitch={gameSwitchList}
                eurojackpotVariant={id === ID_EUROJACKPOT && external}
                showBackButton={
                  !iframe ||
                  currentStep !== 'Coupons' ||
                  this.props.currentCouponId !== 0
                }
              />
          )}

          <div className="Game__container">
            {(id === gluecksSpirale.id || id === ID_EUROJACKPOT) && external && (
              <div className="Game__external-back-button">
                <Button
                  variant={Button.Variant.Back}
                  onClick={this.handlePrevStep}
                >
                  {this.backButtonLabel}
                </Button>
              </div>
            )}

            {Object.keys(this.getStepComponentsVariant())
              .filter((step) => step === currentStep)
              .map((step) => {
                const StepComponent = this.getStepComponentsVariant()[step];
                return (
                  <StepTransition variant={transition} key={step}>
                    <StepComponent
                      onClickBackButton={this.handlePrevStep}
                      quickTipResponses={this.state.quickTipResponses}
                      {...{ ...props, ...{ viewSize: this.state.viewSize } }}
                    />
                  </StepTransition>
                );
              })}
          </div>
        </div>

        <ModalInvalidCouponsInfo id="ModalInvalidCouponsInfo" />
        <ModalGameSwitch
          id="ModalGameSwitch"
          gameSwitch={gameSwitchList}
          currentGame={this.props.id}
        />
        {!external && !iframe && (
          <GameFooter
            shortname={shortname}
            title={title}
            seoHeadline={seoHeadline}
            seoDescription={seoDescription}
          />
        )}
      </div>
    );
  }

  private get jackpotDefaultLabel(): string {
    const { jackpotDesc, jackpotDescGame } = this.props;
    return jackpotDescGame ? jackpotDescGame : jackpotDesc;
  }

  private get backButtonLabel(): JSX.Element | undefined {
    const { currentStep, steps, currentCouponId, external, iframe } =
      this.props;

    const endGameButtonLabel =
      !external && !iframe ? (
        <>
          <Icon variant={IconVariant.Close} />
          <span className="h-hide-xxs--down">Spiel beenden</span>
        </>
      ) : undefined;

    const backStepButtonLabel = (
      <>
        <Icon variant={IconVariant.ArrowLeft} />
        <span className="h-hide-xxs--down">
          {currentStep === GameStep.Coupons ||
          currentStep === steps[steps.indexOf(GameStep.Coupons) + 1]
            ? 'Übersicht'
            : 'Zurück'}
        </span>
      </>
    );

    return (
      steps &&
      (currentStep === steps[0] && currentCouponId === 0
        ? endGameButtonLabel
        : backStepButtonLabel)
    );
  }

  private get isInvalidCoupon(): boolean {
    if (!this.props.couponGroup || this.props.currentCouponId === 0) {
      return false;
    }

    const calculated = calculateCouponProgress(
      this.props.couponGroup.numberGroups,
      this.props.crossedNumbers[this.props.currentCouponId]
    );

    return (
      this.props.currentCouponId > 0 &&
      this.props.currentStep === GameStep.Coupons &&
      calculated !== 100 &&
      calculated !== 0
    );
  }

  private parseQueryParams(): void {
    const gameQueryParamsParser = new GameQueryParamsParser(this.props);
    const removeParamsAfterParams = !this.props.iframe;
    gameQueryParamsParser.parse(removeParamsAfterParams);
  }

  private updatePlayNumber(): void {
    const { id, playNumber, selectedPlayNumber, onPlayNumbersUpdate } =
      this.props;
    if (
      playNumber &&
      ((selectedPlayNumber || []).length === 0 ||
        selectedPlayNumber.every((n) => n === 0))
    ) {
      onPlayNumbersUpdate(
        id,
        getRandomNumberArray(
          playNumber.amountNumbers,
          playNumber.valueMin,
          playNumber.valueMax
        )
      );
    }
  }

  private handleResize = (): void => {
    if (isIOS()) {
      // fix width bug on iOS after resize
      setTimeout(this.updateActualViewSize, 15);
    } else {
      this.updateActualViewSize();
    }
  };

  private updateActualViewSize = (): void => {
    const actualWidth =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth ||
      document.body.offsetWidth;

    const breakpoint =
      actualWidth >= 882
        ? Breakpoint.Game
        : actualWidth >= 768
          ? Breakpoint.Tablet
          : Breakpoint.Mobile;

    if (this.state.viewSize !== breakpoint) {
      this.setState(() => ({
        viewSize: breakpoint,
      }));
    }
  };

  private handleNextStep = (): void => {
    const next = this.getStep(StepDirection.Next);
    if (next) {
      this.updateStepTransition(TransitionVariant.SlideLeft);
      this.updateCurrentStep(next);

      pushDatalayer(
        `virtPath`,
        `${this.props.urls.game}/${next.toLocaleLowerCase()}`,
        DataLayerEvent.VirtualPageview
      );
    }
  };

  private updateStepTransition = (value: TransitionVariant): void => {
    this.stepTransition = value;
    setFocusIfElementExists('#gameStepSubmitButton');
  };

  /*
  private childFactory = (child) => {
    return React.cloneElement(child, {
      classNames: this.stepTransition,
      timeout: child.props.timeout,
    });
  };
  */

  private updateCurrentDrawDay(): void {
    const { drawDeadlines, id, onDrawDaysUpdate, selectedDrawDays } =
      this.props;

    if (drawDeadlines && (!selectedDrawDays || selectedDrawDays.length === 1)) {
      const nextDrawDay = getNextDrawDay(drawDeadlines).day;
      onDrawDaysUpdate(id, [nextDrawDay]);
    }
  }

  private getStep(direction: StepDirection): GameStep {
    const currentIndex = this.props.steps.indexOf(this.props.currentStep);
    return this.props.steps[
      currentIndex + (direction === StepDirection.Next ? 1 : -1)
    ];
  }

  private goBackBeyondFirstStep(): void {
    this.props.router.push(this.props.urls.overview || '/');
  }

  private updateCurrentStep(value: GameStep): void {
    this.props.onCurrentStepUpdate(this.props.id, value);
  }

  private get gamePrice(): GamePriceType {
    const maxAmountCoupons = this.props.couponGroup
      ? this.props.couponGroup.amountCoupons
      : 0;
    const filledCoupons = this.props.couponGroup
      ? countFilledCoupons(
          this.props.crossedNumbers,
          this.props.couponGroup.numberGroups
        )
      : 0;

    return calculateGamePrice(
      filledCoupons > maxAmountCoupons ? maxAmountCoupons : filledCoupons,
      this.props
    );
  }
}

export default withRouter(Game);
